import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '@topcon/topcon-utils';
import { TeButton, TeContainer, TeContainerSlim, TeDivider, TeModal } from '@topcon/tuicl-react';

import IncomingOrganizationCard from '../../components/Migration/IncomingOrganizationCard';
import NoMatchesEmptyState from '../../components/Migration/NoMatchesEmptyState';
import PotentialMatchRow from '../../components/Migration/PotentialMatchRow';
import { useAcceptCustomerSuggestion } from './customerSuggestions.mutations';
import { needsServiceAgreement, LinkedCustomerSuggestion } from './customerSuggestions.helpers';
import { invalidateCustomerSuggestions } from '../../hooks/queries/migration.queries';
import { useErrorDrawer } from '../../providers/ErrorDrawerContext/error-drawer.context';
import { getErrorStatus } from '../../utils/errorStatus';
import { formatAddress } from '../../utils/formatAddress';
import {
  MigrationCandidateBusinessPartnerBody,
  MigrationCandidateMatchTarget,
} from '../../api/generated/pm';

interface Props {
  suggestion: MigrationCandidateBusinessPartnerBody | null;
  open: boolean;
  onClose: (shouldUpdate: boolean) => void;
  onAddAsNewCustomer: (suggestion: MigrationCandidateBusinessPartnerBody) => void;
  /**
   * Hand-off after a Merge that left the Service Agreement pending: the caller
   * closes this modal and opens the Connect wizard on the *accept response*,
   * which is the only value carrying the newly linked Business Partner.
   */
  onConnect: (merged: LinkedCustomerSuggestion) => void;
}

/**
 * Dealer-scope Review modal: shows the incoming suggestion and its ranked
 * Potential Matches, and resolves it by **Merge** into a selected match — or
 * hands off to the Add-as-new-customer wizard.
 *
 * Shares its presentational pieces with the identity-scope
 * `Companies/ReviewOrganizationModal` via `components/Migration/`, but has no
 * segment control and no "Search for organization" tab: the dealer design
 * offers neither.
 */
function ReviewCustomerSuggestionModal({
  suggestion,
  open,
  onClose,
  onAddAsNewCustomer,
  onConnect,
}: Props) {
  const { t } = useTranslation('common', { keyPrefix: 'customerSuggestions' });
  const notificationService = useNotification();
  const { showError } = useErrorDrawer();
  const [selectedExternalId, setSelectedExternalId] = useState<string | null>(null);

  const { mutateAsync: acceptSuggestion, isPending } = useAcceptCustomerSuggestion();

  const topTargets = suggestion?.matchDetails?.top_targets ?? [];
  const address = formatAddress(suggestion?.address);

  // Reset the selection when the suggestion under review changes.
  useEffect(() => {
    setSelectedExternalId(null);
  }, [suggestion?.id]);

  const handleMerge = async () => {
    if (!suggestion || !selectedExternalId || isPending) return;
    try {
      const merged = await acceptSuggestion({ id: suggestion.id, externalId: selectedExternalId });
      // The merge only links the candidate; the Service Agreement is a separate
      // step. When the accept response says the SA is still pending, guide the
      // user straight into Connect instead of closing. An `accepted` SA needs
      // nothing further, and abandonment after this point is out of scope — the
      // candidate is picked up again through the exact-match Review route.
      if (merged && needsServiceAgreement(merged)) {
        onConnect(merged);
        return;
      }
      onClose(true);
    } catch (error: unknown) {
      // 409: the candidate stopped being pending between fetch and click (the
      // import tool matched it, say). The row on screen is stale, so refresh the
      // list and close rather than inviting a retry that cannot succeed.
      if (getErrorStatus(error) === 409) {
        if (!showError(error)) {
          notificationService.addNotification(
            t('reviewModal.mergeConflict'),
            t('reviewModal.mergeConflictDescription', { name: suggestion.name }),
            'error'
          );
        }
        invalidateCustomerSuggestions();
        onClose(true);
        return;
      }
      if (!showError(error)) {
        notificationService.addNotification(
          t('reviewModal.mergeFailed'),
          t('reviewModal.mergeFailedDescription', { name: suggestion.name }),
          'error'
        );
      }
    }
  };

  return (
    <TeModal
      variant="dark"
      open={open}
      closeMode="button"
      closeable
      modalTitle={t('reviewModal.title')}
      teclose={() => !isPending && onClose(false)}
      contentPadding={0}
      width="l"
    >
      <TeContainer
        backgroundColor="background.primary"
        padding="s"
        column
        grow
        fill
        gap="m"
        overflow="auto"
      >
        <IncomingOrganizationCard
          label={t('reviewModal.incomingOrganizationFrom')}
          name={suggestion?.name ?? ''}
          sourceSystem={suggestion?.sourceSystem}
          address={address}
        />

        <TeContainerSlim column gap="s" fill>
          <TeDivider
            text={
              topTargets.length === 0
                ? t('reviewModal.noMatchesHeading')
                : t('reviewModal.possibleMatchesHeading')
            }
            icon="info"
            textPlacement="center"
            dividerColor="border.primary"
            textColor="text.primary"
          />
          {topTargets.length === 0 ? (
            <NoMatchesEmptyState
              title={t('reviewModal.noMatchesFound')}
              description={t('reviewModal.noMatchesDescription')}
            />
          ) : (
            topTargets.map((target: MigrationCandidateMatchTarget) => (
              <PotentialMatchRow
                key={target.external_id}
                target={target}
                selected={selectedExternalId === target.external_id}
                onSelect={() =>
                  setSelectedExternalId((current) =>
                    current === target.external_id ? null : target.external_id
                  )
                }
                labels={{
                  score: t('reviewModal.matchScore', { score: Math.round(target.score) }),
                  address: t('reviewModal.addressMatched'),
                  businessName: t('reviewModal.businessNameMatched'),
                  domain: t('reviewModal.domainMatched'),
                }}
              />
            ))
          )}
        </TeContainerSlim>
      </TeContainer>

      <TeButton
        slot="tertiary"
        text={t('reviewModal.cancel')}
        variant="neutral"
        size="s"
        disabled={isPending}
        teclick={() => !isPending && onClose(false)}
      />
      <TeButton
        slot="secondary"
        text={t('reviewModal.addAsNewCustomer')}
        variant="neutral"
        size="s"
        disabled={selectedExternalId !== null || isPending}
        teclick={() => suggestion && onAddAsNewCustomer(suggestion)}
      />
      <TeButton
        slot="primary"
        text={t('reviewModal.merge')}
        variant="primary"
        size="s"
        disabled={!selectedExternalId || isPending}
        loading={isPending}
        teclick={handleMerge}
      />
    </TeModal>
  );
}

export default ReviewCustomerSuggestionModal;
